import React from "react";
import {
  Box,
  IconButton,
  Center,
  Popover,
  PopoverHeader,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  VStack,
  Avatar,
  Flex,
  HStack,
  Button,
  Skeleton
} from "@chakra-ui/react";
import { BellIcon } from "@chakra-ui/icons";
import { useMyInvites } from "./hooks/useMyInvites";
import { format } from "date-fns";
import humanizeDuration from "humanize-duration";
function formatDateAsDuration(isoDate) {
  const time = Date.now() - new Date(isoDate).getTime();
  if (time < 60 * 1e3) {
    return "A few seconds ago";
  } else if (time < 24 * 60 * 60 * 1e3) {
    const duration = humanizeDuration(time, { largest: 1, round: true });
    return `${duration} ago`;
  }
  return format(isoDate, "MMMM do");
}
function InviteRow({ invite, onAccept, onDecline }) {
  return /* @__PURE__ */ React.createElement(
    Flex,
    {
      key: invite.id,
      alignItems: "center",
      justifyContent: "space-between",
      p: "12px"
    },
    /* @__PURE__ */ React.createElement(
      Avatar,
      {
        size: "md",
        name: invite.organization_name || invite.organization_id,
        src: invite.organization_logo_url,
        alignSelf: "flex-start",
        borderRadius: 5,
        getInitials: (name) => name.substring(0, 2)
      }
    ),
    /* @__PURE__ */ React.createElement(VStack, { spacing: 0, ml: 3, alignItems: "start", w: "full" }, /* @__PURE__ */ React.createElement(Text, { fontWeight: "500", noOfLines: 1, mb: "4px" }, formatDateAsDuration(invite.created_at), " \u2022 Organization Invite"), /* @__PURE__ */ React.createElement(
      Box,
      {
        fontSize: "sm",
        borderWidth: 0.5,
        borderRadius: "md",
        borderColor: "gray.100",
        backgroundColor: "gray.50",
        p: "12px",
        w: "full"
      },
      /* @__PURE__ */ React.createElement(Text, { mb: "8px" }, invite.inviter_name || "A team member", " has invited you to join ", invite.organization_name || "an organization", "."),
      /* @__PURE__ */ React.createElement(HStack, { spacing: 3 }, /* @__PURE__ */ React.createElement(
        Button,
        {
          size: "md",
          colorScheme: "blue",
          onClick: () => onAccept(invite.id)
        },
        "Accept invite"
      ), /* @__PURE__ */ React.createElement(
        Button,
        {
          size: "md",
          colorScheme: "red",
          color: "red.600",
          variant: "outline",
          onClick: () => onDecline(invite.id)
        },
        "Reject invite"
      ))
    ))
  );
}
export const InvitesPopover = () => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const {
    invites,
    accept,
    decline,
    hasNew,
    newCount,
    markAllSeen,
    loading,
    error
  } = useMyInvites();
  const onModalClose = () => {
    onClose();
    if (hasNew) markAllSeen();
  };
  const onInviteAccept = (inviteId) => {
    accept(inviteId);
    onClose();
  };
  const onInviteDecline = (inviteId) => {
    decline(inviteId);
    onClose();
  };
  return /* @__PURE__ */ React.createElement(Popover, { isOpen, onClose: onModalClose, placement: "bottom-end" }, /* @__PURE__ */ React.createElement(PopoverTrigger, null, /* @__PURE__ */ React.createElement(Center, { mr: "12px" }, /* @__PURE__ */ React.createElement(Skeleton, { borderRadius: "md", isLoaded: !loading }, /* @__PURE__ */ React.createElement(Box, { position: "relative", display: "inline-block" }, /* @__PURE__ */ React.createElement(
    IconButton,
    {
      onClick: onToggle,
      size: "lg",
      color: "gray.600",
      borderColor: "gray.300",
      borderWidth: "1px",
      padding: 0,
      disabled: loading,
      icon: /* @__PURE__ */ React.createElement(BellIcon, { boxSize: 8 })
    }
  ), hasNew && /* @__PURE__ */ React.createElement(
    Box,
    {
      position: "absolute",
      top: 2,
      right: 2,
      width: "6px",
      height: "6px",
      bg: "red.400",
      borderRadius: "full",
      zIndex: "1",
      pointerEvents: "none"
    }
  ))))), /* @__PURE__ */ React.createElement(PopoverContent, { minWidth: { base: "90vw", md: "400px" } }, /* @__PURE__ */ React.createElement(PopoverHeader, null, /* @__PURE__ */ React.createElement(VStack, { spacing: 0, px: "12px", alignItems: "start" }, /* @__PURE__ */ React.createElement(Text, { size: "md", fontWeight: "bold" }, "Notifications"), hasNew > 0 && /* @__PURE__ */ React.createElement(Text, { size: "md" }, "You have ", newCount, " new notifications"))), /* @__PURE__ */ React.createElement(
    PopoverBody,
    {
      textStyle: "body-3",
      color: "ionDark",
      maxH: { base: "60vh", md: "540px" },
      overflowY: "auto"
    },
    invites.length > 0 ? /* @__PURE__ */ React.createElement(VStack, { spacing: 2, alignItems: "stretch" }, invites.map((invite) => /* @__PURE__ */ React.createElement(
      InviteRow,
      {
        key: invite.id,
        invite,
        onAccept: onInviteAccept,
        onDecline: onInviteDecline
      }
    ))) : error ? /* @__PURE__ */ React.createElement(
      Box,
      {
        p: "12px",
        bgColor: "red.50",
        borderWidth: "1px",
        borderColor: "red.100",
        borderRadius: "md"
      },
      /* @__PURE__ */ React.createElement(Text, { color: "red.600", fontWeight: 500 }, error)
    ) : /* @__PURE__ */ React.createElement(
      Box,
      {
        p: "12px",
        bgColor: "gray.50",
        borderWidth: "1px",
        borderColor: "gray.100",
        borderRadius: "md"
      },
      /* @__PURE__ */ React.createElement(Text, { color: "gray.500", fontWeight: 500 }, "No notifications available")
    )
  )));
};
